// 5-Element Constitution Quiz data
// Each question maps each answer to one element.

export const ELEMENTS = ["Wood", "Fire", "Earth", "Metal", "Water"];

export const QUIZ_QUESTIONS = [
  {
    q: "When life gets stressful, my first reaction is to…",
    a: [
      { t: "Push harder, get frustrated, get it done.", e: "Wood" },
      { t: "Talk it out, laugh, find connection.", e: "Fire" },
      { t: "Worry, overthink, want to take care of everyone.", e: "Earth" },
      { t: "Withdraw, get organised, control what I can.", e: "Metal" },
      { t: "Go quiet, conserve energy, ride it out.", e: "Water" },
    ],
  },
  {
    q: "My body most often complains in…",
    a: [
      { t: "Tight jaw, headaches, tension in the sides of the body.", e: "Wood" },
      { t: "Racing heart, insomnia, flushed cheeks.", e: "Fire" },
      { t: "Digestion, bloating, heaviness or sweet cravings.", e: "Earth" },
      { t: "Lungs, sinuses, dry skin or grief in the chest.", e: "Metal" },
      { t: "Lower back, knees, ears, deep fatigue.", e: "Water" },
    ],
  },
  {
    q: "The season I feel most alive in is…",
    a: [
      { t: "Spring — green, restless, ready.", e: "Wood" },
      { t: "Summer — long evenings, bright, social.", e: "Fire" },
      { t: "Late summer — golden, slow, full.", e: "Earth" },
      { t: "Autumn — crisp, clear, contemplative.", e: "Metal" },
      { t: "Winter — quiet, deep, internal.", e: "Water" },
    ],
  },
  {
    q: "On my best day, friends describe me as…",
    a: [
      { t: "A visionary doer.", e: "Wood" },
      { t: "Warm, playful, magnetic.", e: "Fire" },
      { t: "Nurturing, steady, generous.", e: "Earth" },
      { t: "Refined, precise, principled.", e: "Metal" },
      { t: "Wise, mysterious, deep.", e: "Water" },
    ],
  },
  {
    q: "The flavour I'm most drawn to is…",
    a: [
      { t: "Sour — lemon, vinegar, fermented.", e: "Wood" },
      { t: "Bitter — coffee, dark chocolate, greens.", e: "Fire" },
      { t: "Sweet — root vegetables, rice, dates.", e: "Earth" },
      { t: "Pungent — ginger, garlic, mint.", e: "Metal" },
      { t: "Salty — sea vegetables, miso, broth.", e: "Water" },
    ],
  },
  {
    q: "When I am out of balance, I most often feel…",
    a: [
      { t: "Angry or impatient.", e: "Wood" },
      { t: "Anxious or scattered.", e: "Fire" },
      { t: "Stuck in worry loops.", e: "Earth" },
      { t: "Grief-stricken or critical.", e: "Metal" },
      { t: "Afraid or exhausted.", e: "Water" },
    ],
  },
  {
    q: "A perfect afternoon looks like…",
    a: [
      { t: "A long hike, a project, a plan coming together.", e: "Wood" },
      { t: "Lunch with friends that turns into dinner.", e: "Fire" },
      { t: "Cooking a slow meal for the people I love.", e: "Earth" },
      { t: "A clean room, a good book, a quiet hour.", e: "Metal" },
      { t: "A nap, a bath, the sound of rain.", e: "Water" },
    ],
  },
];

export const ELEMENT_PROFILES = {
  Wood: {
    title: "Wood",
    season: "Spring",
    organ: "Liver & Gallbladder",
    emotion: "Movement vs. Anger",
    headline: "You are the Visionary — strong, growing, ready to leap.",
    description:
      "Wood types are the architects and athletes of the elements. When in balance, you are decisive, kind and full of forward motion. Out of balance, tension lives in your jaw, shoulders and the sides of your body.",
    foods: ["Leafy greens", "Lemon water", "Sprouts", "Mung beans", "Sour cherries"],
    herbs: ["Bupleurum", "Peppermint", "Dandelion root", "Schisandra"],
    teas: ["Chrysanthemum & goji", "Spearmint", "Lemon balm"],
    points: [
      { name: "LV 3 (Tai Chong)", how: "On the top of the foot, between the big toe and second toe. Press for 1 min each foot." },
      { name: "GB 20 (Feng Chi)", how: "Base of the skull, two thumb-widths from spine. Gentle circles for 30 sec." },
    ],
    mantra: "I bend so I do not break. There is room for me to grow.",
    color: "#3A5A40",
  },
  Fire: {
    title: "Fire",
    season: "Summer",
    organ: "Heart & Small Intestine",
    emotion: "Joy vs. Anxiety",
    headline: "You are the Connector — warm, expressive, alive in love.",
    description:
      "Fire types light up rooms. When in balance, you are joyful, articulate and intimate. Out of balance, you scatter, lose sleep, and feel the heart race.",
    foods: ["Bitter greens", "Cacao", "Watermelon", "Mung bean soup", "Lotus seed"],
    herbs: ["Reishi", "Salvia (Dan Shen)", "Polygala", "Albizia"],
    teas: ["Rose & jujube", "Chamomile", "Lotus leaf"],
    points: [
      { name: "HT 7 (Shen Men)", how: "Inner wrist crease, pinky side. Press softly for 1 min to calm the spirit." },
      { name: "PC 6 (Nei Guan)", how: "Three finger-widths below the inner wrist. Press for anxiety and palpitations." },
    ],
    mantra: "My heart is safe. I can be warm without burning.",
    color: "#B8866D",
  },
  Earth: {
    title: "Earth",
    season: "Late Summer",
    organ: "Spleen & Stomach",
    emotion: "Care vs. Worry",
    headline: "You are the Nurturer — grounded, generous, the centre.",
    description:
      "Earth types are the soft, steady centre of a life. When in balance, you are nourishing, dependable and full of belonging. Out of balance, worry loops, bloating and over-giving creep in.",
    foods: ["Sweet potato", "Pumpkin", "Millet", "Congee", "Dates"],
    herbs: ["Codonopsis", "Atractylodes", "Astragalus", "Liquorice root"],
    teas: ["Ginger & jujube", "Roasted barley", "Tangerine peel"],
    points: [
      { name: "ST 36 (Zu San Li)", how: "Four finger-widths below the kneecap, one finger lateral to the shin. Press for energy and digestion." },
      { name: "SP 6 (San Yin Jiao)", how: "Four finger-widths above the inner ankle bone. Press for grounding." },
    ],
    mantra: "I am held by the same earth I hold others on.",
    color: "#A67C52",
  },
  Metal: {
    title: "Metal",
    season: "Autumn",
    organ: "Lung & Large Intestine",
    emotion: "Reverence vs. Grief",
    headline: "You are the Refiner — clear, principled, precise.",
    description:
      "Metal types love clean lines and quiet rooms. When in balance, you are dignified, discerning, and able to let go. Out of balance, grief, dryness and rigidity settle in the chest.",
    foods: ["White radish", "Pear", "Almonds", "White rice congee", "Lily bulb"],
    herbs: ["Astragalus", "Ophiopogon", "Fritillaria", "Mulberry leaf"],
    teas: ["Pear & almond", "Lily bulb", "White peony"],
    points: [
      { name: "LU 9 (Tai Yuan)", how: "Inner wrist crease, thumb side. Gentle press for breath and immunity." },
      { name: "LI 4 (He Gu)", how: "Web between thumb and index finger. Press for clearing and release." },
    ],
    mantra: "I keep what is precious. I release what is finished.",
    color: "#6B7A66",
  },
  Water: {
    title: "Water",
    season: "Winter",
    organ: "Kidney & Bladder",
    emotion: "Wisdom vs. Fear",
    headline: "You are the Sage — deep, still, full of reserves.",
    description:
      "Water types carry quiet depth. When in balance, you are wise, restorative and patient. Out of balance, fear, fatigue and lower-back ache rise to the surface.",
    foods: ["Black sesame", "Black beans", "Walnuts", "Seaweed", "Bone broth"],
    herbs: ["Rehmannia", "Cordyceps", "Cuscuta", "He Shou Wu"],
    teas: ["Goji & longan", "Roasted black bean", "Schisandra"],
    points: [
      { name: "KD 1 (Yong Quan)", how: "Sole of the foot, in the depression behind the ball. Press for grounding." },
      { name: "KD 3 (Tai Xi)", how: "Inner ankle, midway between ankle bone and Achilles. Press to tonify reserves." },
    ],
    mantra: "I am held by the deep. I trust what I cannot see.",
    color: "#2F4858",
  },
};
